import { useMemo, useState } from "react";
import {
    Box, Button, Chip, Divider, Grid, IconButton, ListItemIcon, ListItemText, Menu, MenuItem,
    Skeleton, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography, alpha,
} from "@mui/material";
import {
    Add, ArrowForward, Block, CreditCard, LocationOn, LockOpen, LockOutlined, MoreVert, Search, Storefront,
} from "@mui/icons-material";
import { useMoeda } from "../../../../../core/hooks/useMoeda";
import { IEstabelecimentoOrganizacao } from "../../../../interfaces/IOrganizacao";
import { CorpoCardBox, EstabelecimentoCard, LogoBox, RodapeCardBox, ToolbarBox, VazioBox } from "../../styles";
import { apresentarSituacao, documentoEstabelecimento, formatarEndereco, nomeEstabelecimento } from "../../utils";

type Filtro = "TODOS" | "ATIVOS" | "BLOQUEADOS" | "SEM_ASSINATURA";

interface IAbaEstabelecimentosProps {
    estabelecimentos: IEstabelecimentoOrganizacao[];
    carregando: boolean;
    entrando: boolean;
    onEntrar: (estabelecimentoUuid: string) => void;
    onGerenciarAssinatura: (estabelecimento: IEstabelecimentoOrganizacao) => void;
    onAlterarBloqueio: (estabelecimento: IEstabelecimentoOrganizacao, bloquear: boolean) => void;
    onNovo: () => void;
}

const FILTROS: { valor: Filtro; rotulo: string }[] = [
    { valor: "TODOS", rotulo: "Todos" },
    { valor: "ATIVOS", rotulo: "Com acesso" },
    { valor: "BLOQUEADOS", rotulo: "Sem acesso" },
    { valor: "SEM_ASSINATURA", rotulo: "Sem assinatura" },
];

export const AbaEstabelecimentos = ({
    estabelecimentos, carregando, entrando, onEntrar, onGerenciarAssinatura, onAlterarBloqueio, onNovo,
}: IAbaEstabelecimentosProps) => {
    const { real } = useMoeda();

    const [busca, setBusca] = useState("");
    const [filtro, setFiltro] = useState<Filtro>("TODOS");
    const [menu, setMenu] = useState<{ ancora: HTMLElement; estabelecimento: IEstabelecimentoOrganizacao }>();

    const visiveis = useMemo(() => {
        const termo = busca.trim().toLowerCase();

        return estabelecimentos.filter(estabelecimento => {
            const situacao = estabelecimento.situacao;

            if (filtro === "ATIVOS" && situacao?.bloqueado) return false;
            if (filtro === "BLOQUEADOS" && !situacao?.bloqueado) return false;
            if (filtro === "SEM_ASSINATURA" && situacao?.situacaoAssinatura !== "SEM_ASSINATURA") return false;

            if (!termo) return true;

            return [nomeEstabelecimento(estabelecimento), estabelecimento.cnpj, estabelecimento.cpf, situacao?.planoNome]
                .some(campo => campo?.toLowerCase().includes(termo));
        });
    }, [estabelecimentos, busca, filtro]);

    const fecharMenu = () => setMenu(undefined);

    const agirNoMenu = (acao: (estabelecimento: IEstabelecimentoOrganizacao) => void) => () => {
        if (menu) {
            acao(menu.estabelecimento);
        }
        fecharMenu();
    };

    if (carregando) {
        return (
            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                {Array.from({ length: 6 }, (_, indice) => (
                    <Grid item xs={12} sm={6} md={4} key={indice}>
                        <Skeleton variant="rounded" height={230} sx={{ borderRadius: "20px" }} />
                    </Grid>
                ))}
            </Grid>
        );
    }

    if (!estabelecimentos.length) {
        return (
            <VazioBox>
                <Storefront sx={{ fontSize: 48, color: "text.disabled" }} />
                <Typography variant="h6">Nenhum estabelecimento ainda</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                    Cadastre o primeiro estabelecimento da organização para começar a gerenciar planos, assinaturas e acessos.
                </Typography>
                <Button variant="contained" startIcon={<Add />} onClick={onNovo} sx={{ mt: 1 }}>
                    Novo estabelecimento
                </Button>
            </VazioBox>
        );
    }

    return (
        <>
            <ToolbarBox>
                <TextField
                    size="small"
                    placeholder="Buscar por nome, documento ou plano"
                    value={busca}
                    onChange={(evento) => setBusca(evento.target.value)}
                    InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: "text.disabled" }} /> }}
                    sx={{ minWidth: 300, flex: 1, maxWidth: 420 }}
                />
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={filtro}
                    onChange={(_, valor: Filtro | null) => valor && setFiltro(valor)}
                >
                    {FILTROS.map(opcao => (
                        <ToggleButton key={opcao.valor} value={opcao.valor} sx={{ textTransform: "none", px: 1.5 }}>
                            {opcao.rotulo}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
                <Box sx={{ flex: 1 }} />
                <Button variant="contained" startIcon={<Add />} onClick={onNovo}>
                    Novo estabelecimento
                </Button>
            </ToolbarBox>

            {!visiveis.length ? (
                <VazioBox>
                    <Search sx={{ fontSize: 40, color: "text.disabled" }} />
                    <Typography variant="body2" color="text.secondary">
                        Nenhum estabelecimento corresponde ao filtro.
                    </Typography>
                </VazioBox>
            ) : (
                <Grid container spacing={3}>
                    {visiveis.map(estabelecimento => {
                        const situacao = estabelecimento.situacao;
                        const apresentacao = apresentarSituacao(situacao);
                        const bloqueado = !!situacao?.bloqueado;
                        const endereco = formatarEndereco(estabelecimento.endereco);
                        const documento = documentoEstabelecimento(estabelecimento);

                        return (
                            <Grid item xs={12} sm={6} md={4} key={estabelecimento.uuid}>
                                <EstabelecimentoCard variant="outlined" statusColor={apresentacao.hex}>
                                    <CorpoCardBox>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                                            <LogoBox>
                                                <Storefront sx={{ color: "text.secondary" }} />
                                            </LogoBox>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                                    {nomeEstabelecimento(estabelecimento)}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" noWrap display="block">
                                                    {documento ?? "Documento não informado"}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                                            <Tooltip
                                                arrow
                                                title={apresentacao.motivos.length ? (
                                                    <Box
                                                        component="ul"
                                                        sx={{
                                                            m: 0,
                                                            pl: apresentacao.motivos.length > 1 ? 2 : 0,
                                                            listStyle: apresentacao.motivos.length > 1 ? "disc" : "none",
                                                        }}
                                                    >
                                                        {apresentacao.motivos.map(motivo => <li key={motivo}>{motivo}</li>)}
                                                    </Box>
                                                ) : ""}
                                            >
                                                <Chip
                                                    size="small"
                                                    label={apresentacao.rotulo}
                                                    sx={{
                                                        backgroundColor: alpha(apresentacao.hex, 0.12),
                                                        color: apresentacao.hex,
                                                        fontWeight: 600,
                                                        cursor: apresentacao.motivos.length ? "help" : "inherit",
                                                    }}
                                                />
                                            </Tooltip>
                                            {situacao?.planoNome && (
                                                <Chip size="small" variant="outlined" color="primary" label={situacao.planoNome} />
                                            )}
                                            {situacao?.freeTier && (
                                                <Chip size="small" color="info" label="Em teste" />
                                            )}
                                        </Box>

                                        <Divider />

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <CreditCard sx={{ fontSize: 16, color: "text.disabled" }} />
                                            <Typography variant="body2" color="text.secondary" noWrap>
                                                {situacao?.valorAssinatura != null
                                                    ? `${real(situacao.valorAssinatura)}/mês${situacao.diaVencimento ? ` · vence dia ${situacao.diaVencimento}` : ""}`
                                                    : "Sem assinatura"}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                            <LocationOn sx={{ fontSize: 16, color: "text.disabled" }} />
                                            <Typography variant="caption" color="text.secondary" noWrap>
                                                {endereco ?? "Endereço não informado"}
                                            </Typography>
                                        </Box>
                                    </CorpoCardBox>

                                    <RodapeCardBox>
                                        {bloqueado ? (
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.disabled" }}>
                                                <LockOutlined sx={{ fontSize: 16 }} />
                                                <Typography variant="caption" fontWeight={600}>Sem acesso</Typography>
                                            </Box>
                                        ) : (
                                            <Button
                                                size="small"
                                                endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                                                disabled={entrando}
                                                onClick={() => onEntrar(estabelecimento.uuid)}
                                            >
                                                Acessar
                                            </Button>
                                        )}

                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            <Button size="small" variant="outlined" onClick={() => onGerenciarAssinatura(estabelecimento)}>
                                                Assinatura
                                            </Button>
                                            <IconButton
                                                size="small"
                                                onClick={(evento) => setMenu({ ancora: evento.currentTarget, estabelecimento })}
                                            >
                                                <MoreVert fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </RodapeCardBox>
                                </EstabelecimentoCard>
                            </Grid>
                        );
                    })}
                </Grid>
            )}

            <Menu anchorEl={menu?.ancora} open={!!menu} onClose={fecharMenu}>
                <MenuItem onClick={agirNoMenu(estabelecimento => onEntrar(estabelecimento.uuid))} disabled={!!menu?.estabelecimento.situacao?.bloqueado}>
                    <ListItemIcon><ArrowForward fontSize="small" /></ListItemIcon>
                    <ListItemText>Acessar estabelecimento</ListItemText>
                </MenuItem>
                <MenuItem onClick={agirNoMenu(onGerenciarAssinatura)}>
                    <ListItemIcon><CreditCard fontSize="small" /></ListItemIcon>
                    <ListItemText>Gerenciar assinatura</ListItemText>
                </MenuItem>
                <Divider />
                {menu?.estabelecimento.situacao?.bloqueadoAdministrativamente ? (
                    <MenuItem onClick={agirNoMenu(estabelecimento => onAlterarBloqueio(estabelecimento, false))}>
                        <ListItemIcon><LockOpen fontSize="small" color="success" /></ListItemIcon>
                        <ListItemText>Liberar acesso</ListItemText>
                    </MenuItem>
                ) : (
                    <MenuItem onClick={agirNoMenu(estabelecimento => onAlterarBloqueio(estabelecimento, true))}>
                        <ListItemIcon><Block fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText>Bloquear acesso</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </>
    );
};
